// frontend/src/components/Ciencia/Ciencia.tsx

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicNav } from '../layout/PublicNav/PublicNav';
import './Ciencia.css';

interface Layer {
  num: string;
  name: string;
  service: string;
  role: string;
  question: string;
}

const LAYERS: Layer[] = [
  { num: '1', name: 'DCP', service: 'autosage-dcp:8002', role: 'Ingestão', question: 'Qual é o dado?' },
  { num: '2', name: 'EDA Explore', service: 'autosage-edaexp:8003', role: 'Diagnóstico', question: 'Como o dado se comporta?' },
  { num: '3', name: 'EDA Prepare', service: 'autosage-edapre:8004', role: 'Decisão', question: 'O que é cientificamente válido?' },
  { num: '4', name: 'KNOW', service: 'autosage-know:8007', role: 'Recomendação', question: 'O que o histórico sugere?' },
  { num: '5', name: 'ML', service: 'autosage-ml:8005', role: 'Execução', question: 'Treinar, avaliar, registrar' },
  { num: '6', name: 'Metrics', service: 'autosage-mtrcs:8001', role: 'Auditoria', question: 'O que aconteceu?' },
];

const ETAPAS = [
  'orquestracao',
  'dcp-sample',
  'llm-target',
  'dcp-run_ingestion',
  'edaexp-run',
  'edapre-run',
  'know-recommend',
  'ml-run',
];

interface Threshold {
  tipo: string;
  metrica: string;
  limiar: string;
  fundamento: string;
}

const THRESHOLDS: Threshold[] = [
  { tipo: 'Classificação', metrica: 'ROC AUC', limiar: '≥ 0,55', fundamento: '0,5 = aleatório. 0,55 exige sinal discriminativo acima do acaso.' },
  { tipo: 'Regressão', metrica: 'R²', limiar: '≥ 0,10', fundamento: '0 = preditor trivial (média). 0,10 exige ao menos 10% da variância.' },
  { tipo: 'Série temporal', metrica: 'MAPE', limiar: '≤ 0,40', fundamento: '40% de erro percentual absoluto médio. Acima, pior que intervalo ingênuo.' },
  { tipo: 'Contagem', metrica: 'Deviance escalada', limiar: '0,5 – 2,0', fundamento: 'Para modelo bem especificado, E[D/(n-p)] ≈ 1.' },
];

const MODELS_BY_PROBLEM = [
  { tipo: 'classification', modelos: 'logistic_regression, random_forest, gradient_boosting, xgboost' },
  { tipo: 'regression', modelos: 'linear_regression, ridge, lasso, elastic_net, random_forest, gradient_boosting, xgboost' },
  { tipo: 'time_series', modelos: 'naive, arima, sarima, prophet, lstm' },
  { tipo: 'count_model', modelos: 'poisson_regression, negative_binomial_regression' },
];

const REFERENCES = [
  'Popper, K. R. (1959). The Logic of Scientific Discovery.',
  'Gelman, A., & Loken, E. (2013). The Statistical Crisis in Science.',
  'Ioannidis, J. P. A. (2005). Why Most Published Research Findings Are False.',
  'Simmons, J. P., Nelson, L. D., & Simonsohn, U. (2011). False-Positive Psychology.',
  'Kaufman, S., Rosset, S., Perlich, C., & Stitelman, O. (2012). Leakage in Data Mining.',
  'Hyndman, R. J., & Athanasopoulos, G. (2021). Forecasting: Principles and Practice.',
  'Bergmeir, C., & Benítez, J. M. (2012). On the use of cross-validation for time series.',
  'McCullagh, P., & Nelder, J. A. (1989). Generalized Linear Models.',
  'Cohen, J. (1988). Statistical Power Analysis for the Behavioral Sciences.',
  'Cronbach, L. J., & Meehl, P. E. (1955). Construct Validity in Psychological Tests.',
];

export const Ciencia: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="ciencia-page">
      <PublicNav />
      <div className="ciencia-container">

        <section className="ciencia-hero">
          <div className="ciencia-hero-badge">🔬 AutoSAGE · Visão Científica</div>
          <h1>
            A plataforma de ML
            <br />
            <span className="highlight">cientificamente defensável.</span>
          </h1>
          <p className="ciencia-hero-sub">
            Cada decisão é registrada. Cada modelo é avaliado contra um limiar explícito.
            Cada execução tem um <code>trace_id</code> que conecta todas as camadas.
            O <strong>AutoSAGE</strong> não entrega apenas um modelo —
            entrega <strong>prova</strong> de que o modelo é válido.
          </p>
          <div className="ciencia-hero-actions">
            <button className="btn-ciencia-primary" onClick={() => navigate('/register')}>
              🚀 Experimentar
            </button>
            <button className="btn-ciencia-secondary" onClick={() => navigate('/governanca')}>
              🛡️ Ver governança
            </button>
          </div>
        </section>

        <section className="ciencia-section">
          <h2 className="ciencia-section-title">O princípio fundamental</h2>
          <div className="principio-box">
            <p>
              Em ambientes regulados, a pergunta não é <em>"qual o melhor modelo?"</em>,
              mas <em>"qual o modelo que pode ser defendido?"</em>.
            </p>
            <p>
              O AutoSAGE responde à segunda pergunta — e a resposta é uma
              <strong> cadeia auditável de decisões</strong>.
            </p>
          </div>
          <div className="formula-box">
            <div className="formula-title">Princípio de separabilidade</div>
            <div className="formula-content">
              Para toda execução <em>e</em> ∈ <em>E</em>, existe uma decisão <em>d</em> ∈ <em>D</em> tal que:
              <br />
              &nbsp;&nbsp;&nbsp;&nbsp;<em>e</em> = executa(<em>d</em>)
              <br />
              &nbsp;&nbsp;&nbsp;&nbsp;<em>e</em> não modifica <em>d</em>
              <br />
              &nbsp;&nbsp;&nbsp;&nbsp;<em>d</em> é registrada antes de <em>e</em>
            </div>
          </div>
        </section>

        <section className="ciencia-section">
          <h2 className="ciencia-section-title">As 6 camadas</h2>
          <p className="ciencia-section-sub">Cada camada atua no seu nível semântico, sem inferir comportamento das demais.</p>
          <div className="camadas-list">
            {LAYERS.map((layer) => (
              <div key={layer.num} className="camada-card">
                <div className="camada-num">{layer.num}</div>
                <div className="camada-info">
                  <div className="camada-name">{layer.name}</div>
                  <div className="camada-service"><code>{layer.service}</code></div>
                </div>
                <div className="camada-role">{layer.role}</div>
                <div className="camada-question">"{layer.question}"</div>
              </div>
            ))}
          </div>
        </section>

        <section className="ciencia-section">
          <h2 className="ciencia-section-title">As 8 etapas do pipeline</h2>
          <p className="ciencia-section-sub">Ordem factual extraída do <code>ORDEM_ETAPAS</code> do backend.</p>
          <div className="etapas-grid">
            {ETAPAS.map((etapa, i) => (
              <div key={etapa} className="etapa-card">
                <span className="etapa-num">{i + 1}</span>
                <code className="etapa-name">{etapa}</code>
              </div>
            ))}
          </div>
        </section>

        <section className="ciencia-section">
          <h2 className="ciencia-section-title">Limiares por tipo de problema</h2>
          <p className="ciencia-section-sub">
            Se o modelo não atinge o limiar, o processo é <strong>TERMINATED</strong> — sem re-tentativa automática.
          </p>
          <div className="thresholds-table-wrapper">
            <table className="thresholds-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Métrica</th>
                  <th>Limiar</th>
                  <th>Fundamento</th>
                </tr>
              </thead>
              <tbody>
                {THRESHOLDS.map((t) => (
                  <tr key={t.tipo}>
                    <td className="th-tipo">{t.tipo}</td>
                    <td className="th-metrica">{t.metrica}</td>
                    <td className="th-limiar">{t.limiar}</td>
                    <td className="th-fundamento">{t.fundamento}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="ciencia-section">
          <h2 className="ciencia-section-title">Modelos por tipo de problema</h2>
          <p className="ciencia-section-sub">
            O <code>ALLOWED_MODELS_BY_PROBLEM</code> é decidido no <strong>EDA Prepare</strong>, não no ML.
          </p>
          <div className="models-grid">
            {MODELS_BY_PROBLEM.map((m) => (
              <div key={m.tipo} className="model-card">
                <div className="model-type">{m.tipo}</div>
                <code className="model-list">{m.modelos}</code>
              </div>
            ))}
          </div>
        </section>

        <section className="ciencia-section">
          <h2 className="ciencia-section-title">Validação temporal</h2>
          <div className="two-col">
            <div className="col-card">
              <h3>Walk-forward com step regular</h3>
              <p>
                Para séries temporais, o AutoSAGE usa walk-forward validation
                com step regular: o modelo é re-treinado a cada <code>s</code> pontos,
                cobrindo toda a série.
              </p>
              <div className="formula-box small">
                para t = T_min, T_min + s, ..., N - h:<br />
                &nbsp;&nbsp;&nbsp;&nbsp;modelo = treinar(y₁, ..., yₜ)<br />
                &nbsp;&nbsp;&nbsp;&nbsp;ŷₜ₊ₕ = modelo.prever(h)<br />
                &nbsp;&nbsp;&nbsp;&nbsp;erroₜ = métrica(yₜ₊ₕ, ŷₜ₊ₕ)
              </div>
              <p className="fact-note">
                Com <code>s = 50</code> e <code>N = 6400</code>, são 128 iterações.
                Mínimo de <code>MIN_TRAIN = 10</code> observações.
              </p>
            </div>
            <div className="col-card">
              <h3>Por que CV clássica é inválida</h3>
              <ul>
                <li><strong>Leakage do futuro:</strong> o modelo é treinado com observações que ocorrem depois do ponto de teste.</li>
                <li><strong>Quebra da dependência temporal:</strong> o modelo não aprende a autocorrelação.</li>
                <li><strong>Métrica otimista:</strong> o erro estimado é sistematicamente menor que o real.</li>
              </ul>
              <p className="fact-note">
                Bergmeir &amp; Benítez (2012) — CV clássica é inválida para séries temporais.
              </p>
            </div>
          </div>
        </section>

        <section className="ciencia-section">
          <h2 className="ciencia-section-title">Auditoria por trace_id</h2>
          <p className="ciencia-section-sub">Um único identificador atravessa todas as tabelas de auditoria.</p>
          <div className="auditoria-grid">
            {[
              { icon: '📊', table: 'pipeline_metrics', desc: 'Eventos START/SUCCESS/FAILED por etapa' },
              { icon: '📈', table: 'eda_explore_snapshot', desc: 'Diagnóstico estatístico completo' },
              { icon: '🧹', table: 'eda_prepare_snapshots', desc: 'Decisões de contrato de treino' },
              { icon: '🤖', table: 'ml_experiment_result', desc: 'Resultado final do experimento' },
              { icon: '🧪', table: 'ml_model_training', desc: 'Cada modelo treinado (incl. falhas)' },
              { icon: '📋', table: 'know_recommendation_evidence', desc: 'Evidência da recomendação' },
            ].map((item, i) => (
              <div key={i} className="auditoria-card">
                <span className="auditoria-icon">{item.icon}</span>
                <code className="auditoria-table">{item.table}</code>
                <p className="auditoria-desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="ciencia-section">
          <h2 className="ciencia-section-title">Referências científicas</h2>
          <p className="ciencia-section-sub">Cada decisão de design é ancorada em literatura revisada por pares.</p>
          <ol className="referencias-list">
            {REFERENCES.map((ref, i) => (
              <li key={i}>{ref}</li>
            ))}
          </ol>
        </section>

        <section className="ciencia-cta">
          <h2>Pronto para uma plataforma cientificamente defensável?</h2>
          <p>Cada decisão auditável. Cada modelo com prova. Cada execução com trace_id.</p>
          <div className="ciencia-cta-actions">
            <button className="btn-ciencia-primary" onClick={() => navigate('/register')}>
              🚀 Criar conta
            </button>
            <button className="btn-ciencia-secondary" onClick={() => navigate('/empresas')}>
              🏢 Ver versão para empresas
            </button>
          </div>
        </section>

      </div>
    </div>
  );
};

export default Ciencia;